/**
 * Local hot-seat table (/local?mode=2v2).
 *
 * Plays the pure rules engine on this device — no backend needed. The
 * round-over and match-over banners render into the table's status layer.
 */

import type { End, GameMode } from "@domino/types";
import { teamForSeat } from "@domino/types";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { pipCount } from "@domino/rules-engine";

import { GameTable, LocalTableChat, type SeatInfo } from "../components/GameTable";
import { RoundEnd } from "../components/RoundEnd";
import { displayName } from "../lib/player";
import { useLocalGame } from "../lib/useLocalGame";

const MODES: GameMode[] = ["1v1", "3p", "2v2"];
void MODES;

export default function LocalPage({ mode, target }: { mode: GameMode; target?: number }) {
  const navigate = useNavigate();
  const targetScore = target && Number.isFinite(target) ? Number(target) : 100;
  const {
    state,
    view,
    rules,
    play,
    draw,
    pass,
    nextRound,
    reset,
    scores,
    matchFinished,
    matchWinner,
    matchLoser,
  } = useLocalGame(mode, targetScore);

  const myName = displayName() || "Player 1";
  const seats: SeatInfo[] = useMemo(
    () =>
      Array.from({ length: rules.seatCount }, (_, seatIndex) => ({
        seatIndex,
        name: seatIndex === 0 ? myName : `Player ${seatIndex + 1}`,
        team: teamForSeat(mode, seatIndex),
        count: view.handCounts[seatIndex] ?? 0,
        isTurn: view.currentSeat === seatIndex && view.status === "active",
        isMe: view.mySeat === seatIndex,
      })),
    [mode, myName, rules.seatCount, view.currentSeat, view.handCounts, view.mySeat, view.status],
  );

  const result = view.result;

  return (
    <GameTable
              footer={<LocalTableChat />}
      view={view}
      mode={mode}
      seats={seats}
      scores={scores}
      targetScore={targetScore}
      onPlay={(tileId: number, end?: End) => play(tileId, end)}
      onDraw={draw}
      onPass={pass}
      onNextRound={nextRound}
      canNextRound={!matchFinished}
      title="Hot-seat table"
      onLeave={() => navigate({ to: "/" })}
      statusNote={
        matchFinished ? (
          <div className="verdict verdict-match">
            <span className="verdict-ar">الملتقى خلص!</span>
            <h2 className="verdict-title">
              {mode === "2v2"
                ? `Team ${(matchWinner ?? 0) + 1} takes the match`
                : `${seats.find((s) => s.seatIndex === matchWinner)?.name ?? "The table"} takes the match`}
            </h2>
            <p className="verdict-sub">
              {mode === "2v2" ? `Team ${(matchLoser ?? 0) + 1}` : `Player ${(matchLoser ?? 0) + 1}`}{" "}
              hit {scores[matchLoser ?? 0]} / {targetScore} penalty pips — first to the target
              loses.
            </p>
            <button type="button" className="action-btn action-gold" onClick={reset}>
              læb tāny · إعادة — Rematch
            </button>
          </div>
        ) : result ? (
          <RoundEnd
            mode={mode}
            seats={seats}
            mySeat={-1}
            result={result}
            hands={state.hands.map((hand) => [...hand])}
            pips={state.hands.map((hand) => pipCount(hand))}
          />
        ) : null
      }
    />
  );
}
